<script setup lang="ts">
interface Subcategory {
  id: string
  slug: string
  name: string
}

import { getCategories } from '@shopware-pwa/api-client'
import { Category } from '@shopware-pwa/types'
import { getCategoryPermalink } from '~/lib/'
const { apiInstance } = useShopwareContext()
const localePath = useLocalePath()
const props = defineProps<{
  category: Category
}>()

const { data: children } = await useAsyncData('cmsNavigationChildren' + props.category.id, async () => {
  const response = await getCategories(
    {
      filter: [
        {
          type: 'equals',
          field: 'active',
          value: true as unknown as string,
        },
        {
          type: 'equals',
          field: 'parentId',
          value: props.category.id,
        },
      ],
      sort: [
        {
          field: 'afterCategoryId',
          order: 'asc',
        },
      ],
    },
    apiInstance,
  )

  let child = response.elements.shift()
  if (!child) return []

  const children = new Map(response.elements.map((obj) => [obj.afterCategoryId, obj]))
  const orderedChildren: Subcategory[] = []

  // Ordering in Shopware is done by chaining elements so we need to do some computation to order them.
  orderedChildren.push({
    id: child.id,
    slug: localePath(getCategoryPermalink(child)),
    name: child.translated.name,
  })
  while (children.size) {
    let nextKey = child.id
    if (!nextKey) break

    child = children.get(nextKey)
    if (!child) break
    children.delete(nextKey)
    orderedChildren.push({
      id: child.id,
      slug: localePath(getCategoryPermalink(child)),
      name: child.translated.name,
    })
  }

  return orderedChildren
})

const subcategories =
  children.value?.map((child) => ({
    id: child.id,
    link: {
      text: child.name,
      url: child.slug,
    },
  })) || []
</script>
<template>
  <div class="subcategories">
    <LinkList :items="subcategories" />
  </div>
</template>
<style scoped lang="scss">
.subcategories :deep(.link-list) {
  display: grid;
  gap: 0 var(--gap);
  @include breakpoint(ts) {
    grid-template-columns: 1fr 1fr;
  }
  .link {
    color: var(--c-primary);
    font-weight: 400;
  }
}
</style>
